import { createFileRoute } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Box,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Eye,
  FileText,
  Flashlight,
  Maximize2,
  MessageSquare,
  RotateCcw,
  Scissors,
  Send,
  Sparkles,
  Users,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/prototypes/clash-meeting")({
  component: ClashMeeting,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Severity = "critical" | "major" | "minor";
type ClashStatus = "new" | "in_review" | "resolved" | "deferred" | "accepted";
type ResolutionType =
  | "approve_suggestion"
  | "modify_approve"
  | "defer"
  | "accept_as_is"
  | "needs_info";
type MeetingPhase = "setup" | "walkthrough" | "summary";
type TradeSystem =
  | "Mechanical"
  | "Structural"
  | "Electrical"
  | "Plumbing"
  | "Fire Protection";

interface Attendee {
  id: string;
  name: string;
  trade: TradeSystem | "General Contractor";
  company: string;
  present: boolean;
}

interface ClashElement {
  id: string;
  description: string;
  system: TradeSystem;
  trade: string;
  subcontractor: string;
}

interface ClashGroup {
  id: string;
  title: string;
  intersectionCount: number;
  severity: Severity;
  location: string;
  elements: ClashElement[];
  severityRationale: string;
  aiSuggestion: string;
  aiConfidence: number;
  status: ClashStatus;
  illustration:
    | "beam-duct"
    | "pipe-conduit"
    | "duct-pipe"
    | "sprinkler-duct"
    | "conduit-column"
    | "pipe-beam";
}

interface Decision {
  clashId: string;
  resolution: ResolutionType;
  assigneeId: string;
  dueDate: string;
  notes: string;
  timestamp: number;
}

interface ChatMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
}

// ─── Mock Data ───────────────────────────────────────────────────────────────

const ATTENDEES: Attendee[] = [
  {
    id: "a1",
    name: "Sarah Chen",
    trade: "General Contractor",
    company: "Supplied GC",
    present: true,
  },
  {
    id: "a2",
    name: "Mike Torres",
    trade: "Mechanical",
    company: "AirFlow MEP",
    present: true,
  },
  {
    id: "a3",
    name: "Lisa Park",
    trade: "Structural",
    company: "StrongBuild Engineering",
    present: true,
  },
  {
    id: "a4",
    name: "James Wright",
    trade: "Electrical",
    company: "BrightWire Electric",
    present: true,
  },
  {
    id: "a5",
    name: "Ana Ruiz",
    trade: "Plumbing",
    company: "FlowRight Plumbing",
    present: true,
  },
  {
    id: "a6",
    name: "Dave Kim",
    trade: "Fire Protection",
    company: "SafeGuard FP",
    present: true,
  },
];

const CLASH_GROUPS: ClashGroup[] = [
  {
    id: "CG-001",
    title: "Supply Duct D-4 vs Steel Beam B-14",
    intersectionCount: 5,
    severity: "critical",
    location: "Level 3, Grid C-D / 4-6",
    elements: [
      {
        id: "D-4",
        description: "24×12 Supply Duct Run",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
      {
        id: "B-14",
        description: "W16×40 Steel Beam",
        system: "Structural",
        trade: "Structural",
        subcontractor: "StrongBuild Engineering",
      },
    ],
    severityRationale:
      "Steel beam is load-bearing and cannot be moved or penetrated. Duct must re-route. 5 intersections along an 80′ run suggest the duct elevation conflicts with the entire beam line.",
    aiSuggestion:
      "Lower duct run D-4 by 8″ to clear beam flange. Maintains minimum ceiling height of 8′-6″. No downstream conflicts with plumbing below.",
    aiConfidence: 92,
    status: "new",
    illustration: "beam-duct",
  },
  {
    id: "CG-002",
    title: "Conduit Tray ET-7 vs Concrete Column C-22",
    intersectionCount: 2,
    severity: "critical",
    location: "Level 2, Grid B / 3",
    elements: [
      {
        id: "ET-7",
        description: "4″ Conduit Tray",
        system: "Electrical",
        trade: "Electrical",
        subcontractor: "BrightWire Electric",
      },
      {
        id: "C-22",
        description: "24″×24″ Concrete Column",
        system: "Structural",
        trade: "Structural",
        subcontractor: "StrongBuild Engineering",
      },
    ],
    severityRationale:
      "Concrete column is structural — cannot be modified. Conduit tray routes directly through column volume at two points.",
    aiSuggestion:
      "Offset conduit tray 18″ east to route around column. Requires two 90° bends. Total additional length: 6′.",
    aiConfidence: 88,
    status: "new",
    illustration: "conduit-column",
  },
  {
    id: "CG-003",
    title: "Return Duct D-9 vs 4″ Sanitary Pipe P-3",
    intersectionCount: 3,
    severity: "major",
    location: "Level 3, Grid E-F / 2-3",
    elements: [
      {
        id: "D-9",
        description: "20×10 Return Duct",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
      {
        id: "P-3",
        description: "4″ Sanitary Waste Pipe",
        system: "Plumbing",
        trade: "Plumbing",
        subcontractor: "FlowRight Plumbing",
      },
    ],
    severityRationale:
      "Both elements have some flexibility, but sanitary pipe requires minimum slope. Moving the pipe risks losing drainage grade over a long run.",
    aiSuggestion:
      "Raise duct D-9 by 4″ at three crossing points. Pipe slope maintained at 1/8″ per foot. Duct transitions require two 6″ offsets.",
    aiConfidence: 85,
    status: "new",
    illustration: "duct-pipe",
  },
  {
    id: "CG-004",
    title: "Sprinkler Main SP-2 vs Supply Duct D-6",
    intersectionCount: 4,
    severity: "major",
    location: "Level 2, Grid D-F / 5-7",
    elements: [
      {
        id: "SP-2",
        description: "4″ Sprinkler Main",
        system: "Fire Protection",
        trade: "Fire Protection",
        subcontractor: "SafeGuard FP",
      },
      {
        id: "D-6",
        description: "30×14 Supply Duct",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
    ],
    severityRationale:
      "Sprinkler main must maintain code-required clearance from duct. Current routing has 4 crossings with less than 1″ separation. Fire protection takes priority per code.",
    aiSuggestion:
      "Lower duct D-6 by 6″ at crossings to achieve 3″ clearance. Sprinkler main stays in place per NFPA 13 requirements.",
    aiConfidence: 94,
    status: "new",
    illustration: "sprinkler-duct",
  },
  {
    id: "CG-005",
    title: "2″ Hot Water Supply P-8 vs Conduit Run ET-3",
    intersectionCount: 1,
    severity: "minor",
    location: "Level 4, Grid A / 8",
    elements: [
      {
        id: "P-8",
        description: "2″ Hot Water Supply",
        system: "Plumbing",
        trade: "Plumbing",
        subcontractor: "FlowRight Plumbing",
      },
      {
        id: "ET-3",
        description: "1″ EMT Conduit Run",
        system: "Electrical",
        trade: "Electrical",
        subcontractor: "BrightWire Electric",
      },
    ],
    severityRationale:
      "Single crossing point. Both elements are small and flexible. Minimal rework required.",
    aiSuggestion:
      "Shift conduit ET-3 down 3″ at crossing. No impact to other systems.",
    aiConfidence: 97,
    status: "new",
    illustration: "pipe-conduit",
  },
  {
    id: "CG-006",
    title: "Exhaust Duct D-11 vs Steel Beam B-8",
    intersectionCount: 3,
    severity: "critical",
    location: "Level 2, Grid A-B / 1-3",
    elements: [
      {
        id: "D-11",
        description: "18×10 Exhaust Duct",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
      {
        id: "B-8",
        description: "W14×30 Steel Beam",
        system: "Structural",
        trade: "Structural",
        subcontractor: "StrongBuild Engineering",
      },
    ],
    severityRationale:
      "Load-bearing beam cannot move. Exhaust duct elevation was set before structural rev 3 changed beam depth from W12 to W14.",
    aiSuggestion:
      "Re-route exhaust duct under beam with a 10″ drop and recovery. Check kitchen hood static pressure — may need fan upsizing.",
    aiConfidence: 74,
    status: "new",
    illustration: "beam-duct",
  },
  {
    id: "CG-007",
    title: "3″ Storm Drain P-12 vs W12×26 Beam B-19",
    intersectionCount: 2,
    severity: "major",
    location: "Level R, Grid C / 5-6",
    elements: [
      {
        id: "P-12",
        description: "3″ Storm Drain",
        system: "Plumbing",
        trade: "Plumbing",
        subcontractor: "FlowRight Plumbing",
      },
      {
        id: "B-19",
        description: "W12×26 Steel Beam",
        system: "Structural",
        trade: "Structural",
        subcontractor: "StrongBuild Engineering",
      },
    ],
    severityRationale:
      "Storm drain requires slope to roof drain. Beam was raised in rev 4. Pipe can potentially be sleeved through beam web if engineer approves.",
    aiSuggestion:
      "Request structural engineer approval for two 4″ web penetrations with reinforcing. Fallback: re-route drain 12′ east to miss beam.",
    aiConfidence: 68,
    status: "new",
    illustration: "pipe-beam",
  },
  {
    id: "CG-008",
    title: "Branch Sprinkler SP-5 vs Return Duct D-14",
    intersectionCount: 6,
    severity: "major",
    location: "Level 3, Grid B-D / 7-9",
    elements: [
      {
        id: "SP-5",
        description: "2″ Branch Sprinkler Lines",
        system: "Fire Protection",
        trade: "Fire Protection",
        subcontractor: "SafeGuard FP",
      },
      {
        id: "D-14",
        description: "24×12 Return Duct",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
    ],
    severityRationale:
      "6 branch lines cross the duct in a tenant space. Sprinkler heads must maintain coverage pattern — moving branches shifts head locations.",
    aiSuggestion:
      "Raise duct D-14 by 4″ to clear branch lines. Verify sprinkler deflector-to-ceiling distance stays within 1-12″ range per NFPA 13.",
    aiConfidence: 81,
    status: "new",
    illustration: "sprinkler-duct",
  },
  {
    id: "CG-009",
    title: "Main Electrical Feed ET-1 vs 6″ Chilled Water P-1",
    intersectionCount: 2,
    severity: "major",
    location: "Level 1, Grid F / 2-3",
    elements: [
      {
        id: "ET-1",
        description: "4″ Main Electrical Conduit",
        system: "Electrical",
        trade: "Electrical",
        subcontractor: "BrightWire Electric",
      },
      {
        id: "P-1",
        description: "6″ Chilled Water Supply",
        system: "Plumbing",
        trade: "Plumbing",
        subcontractor: "FlowRight Plumbing",
      },
    ],
    severityRationale:
      "Code requires 12″ separation between electrical and water piping. Current separation is 3″ at two crossing points.",
    aiSuggestion:
      "Drop electrical conduit 14″ at crossings to achieve code-required separation. Verify no conflicts with cable tray below.",
    aiConfidence: 86,
    status: "new",
    illustration: "pipe-conduit",
  },
  {
    id: "CG-010",
    title: "VAV Box D-20 vs Sprinkler Riser SP-1",
    intersectionCount: 1,
    severity: "minor",
    location: "Level 2, Grid E / 4",
    elements: [
      {
        id: "D-20",
        description: "VAV Terminal Box",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
      {
        id: "SP-1",
        description: "4″ Sprinkler Riser",
        system: "Fire Protection",
        trade: "Fire Protection",
        subcontractor: "SafeGuard FP",
      },
    ],
    severityRationale:
      "VAV box access panel conflicts with riser. Not a hard clash but blocks maintenance access.",
    aiSuggestion:
      "Shift VAV box 24″ south. Access panel clears riser with 8″ to spare. Flex duct connection adjusts automatically.",
    aiConfidence: 91,
    status: "new",
    illustration: "sprinkler-duct",
  },
  {
    id: "CG-011",
    title: "Light Fixture Conduit ET-12 vs Supply Duct D-4",
    intersectionCount: 8,
    severity: "minor",
    location: "Level 3, Grid C-F / 4-6",
    elements: [
      {
        id: "ET-12",
        description: "3/4″ MC Cable (Lighting)",
        system: "Electrical",
        trade: "Electrical",
        subcontractor: "BrightWire Electric",
      },
      {
        id: "D-4",
        description: "24×12 Supply Duct Run",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
    ],
    severityRationale:
      "Flexible MC cable — easy to reroute around duct. 8 crossings but all minor. Typically resolved in the field.",
    aiSuggestion:
      "Route MC cable along duct flange using J-hooks. Standard field coordination — no drawing revision needed.",
    aiConfidence: 98,
    status: "new",
    illustration: "beam-duct",
  },
  {
    id: "CG-012",
    title: "Kitchen Exhaust D-15 vs 4″ Gas Pipe P-20",
    intersectionCount: 1,
    severity: "critical",
    location: "Level 1, Grid B / 1",
    elements: [
      {
        id: "D-15",
        description: "20×16 Kitchen Exhaust Duct",
        system: "Mechanical",
        trade: "Mechanical",
        subcontractor: "AirFlow MEP",
      },
      {
        id: "P-20",
        description: "4″ Gas Pipe",
        system: "Plumbing",
        trade: "Plumbing",
        subcontractor: "FlowRight Plumbing",
      },
    ],
    severityRationale:
      "Gas pipe running through kitchen exhaust duct is a code violation and safety hazard. Must be separated immediately.",
    aiSuggestion:
      "Re-route gas pipe P-20 outside exhaust duct envelope with minimum 6″ clearance. Requires new penetration through wall at Grid B/1.",
    aiConfidence: 96,
    status: "new",
    illustration: "duct-pipe",
  },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<
  Severity,
  { label: string; color: string; bg: string }
> = {
  critical: {
    label: "Critical",
    color: "text-red-700 dark:text-red-400",
    bg: "bg-red-100 dark:bg-red-950 border-red-200 dark:border-red-900",
  },
  major: {
    label: "Major",
    color: "text-amber-700 dark:text-amber-400",
    bg: "bg-amber-100 dark:bg-amber-950 border-amber-200 dark:border-amber-900",
  },
  minor: {
    label: "Minor",
    color: "text-blue-700 dark:text-blue-400",
    bg: "bg-blue-100 dark:bg-blue-950 border-blue-200 dark:border-blue-900",
  },
};

const RESOLUTION_LABELS: Record<ResolutionType, string> = {
  approve_suggestion: "Approve AI Suggestion",
  modify_approve: "Modify & Approve",
  defer: "Defer to Next Meeting",
  accept_as_is: "Accept As-Is",
  needs_info: "Needs More Info",
};

const TRADE_COLORS: Record<string, string> = {
  Mechanical:
    "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  Structural:
    "bg-slate-200 text-slate-800 dark:bg-slate-800 dark:text-slate-200",
  Electrical:
    "bg-yellow-100 text-yellow-800 dark:bg-yellow-950 dark:text-yellow-300",
  Plumbing: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  "Fire Protection":
    "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
};

function formatDueDate(daysFromNow: number): string {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toISOString().split("T")[0]!;
}

// ─── 3D Viewport Mock ───────────────────────────────────────────────────────

function ViewportMock({ clash }: { clash: ClashGroup }) {
  const illustrations: Record<ClashGroup["illustration"], React.ReactNode> = {
    "beam-duct": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        {/* Grid lines */}
        <defs>
          <pattern
            id="grid"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid)" />
        {/* Steel beam - horizontal */}
        <g>
          <rect x="40" y="110" width="320" height="8" fill="#64748b" rx="1" />
          <rect x="40" y="118" width="320" height="40" fill="#475569" rx="1" />
          <rect x="40" y="158" width="320" height="8" fill="#64748b" rx="1" />
          <text
            x="370"
            y="142"
            fill="#94a3b8"
            fontSize="11"
            textAnchor="end"
            fontFamily="monospace"
          >
            B-14 (W16×40)
          </text>
        </g>
        {/* Duct - crossing at angle */}
        <g>
          <rect
            x="120"
            y="80"
            width="60"
            height="120"
            fill="rgba(249,115,22,0.4)"
            stroke="#f97316"
            strokeWidth="2"
            strokeDasharray="6 3"
            rx="2"
          />
          <text
            x="150"
            y="220"
            fill="#fb923c"
            fontSize="11"
            textAnchor="middle"
            fontFamily="monospace"
          >
            D-4 (24×12)
          </text>
        </g>
        {/* Clash zone */}
        <g>
          <rect
            x="120"
            y="108"
            width="60"
            height="60"
            fill="rgba(239,68,68,0.2)"
            stroke="#ef4444"
            strokeWidth="2"
            rx="4"
          />
          <circle
            cx="150"
            cy="138"
            r="12"
            fill="none"
            stroke="#ef4444"
            strokeWidth="2"
          >
            <animate
              attributeName="r"
              values="10;16;10"
              dur="2s"
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="1;0.3;1"
              dur="2s"
              repeatCount="indefinite"
            />
          </circle>
          <text
            x="150"
            y="100"
            fill="#ef4444"
            fontSize="10"
            textAnchor="middle"
            fontWeight="bold"
          >
            CLASH
          </text>
        </g>
        {/* Suggested position */}
        <rect
          x="120"
          y="170"
          width="60"
          height="120"
          fill="rgba(34,197,94,0.15)"
          stroke="#22c55e"
          strokeWidth="1.5"
          strokeDasharray="4 4"
          rx="2"
        />
        <text
          x="190"
          y="240"
          fill="#22c55e"
          fontSize="10"
          fontFamily="monospace"
        >
          ↓ Suggested: -8″
        </text>
      </svg>
    ),
    "pipe-conduit": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <pattern
            id="grid2"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid2)" />
        {/* Pipe - horizontal */}
        <rect
          x="30"
          y="135"
          width="340"
          height="16"
          fill="rgba(59,130,246,0.5)"
          stroke="#3b82f6"
          strokeWidth="2"
          rx="8"
        />
        <text
          x="380"
          y="148"
          fill="#60a5fa"
          fontSize="11"
          textAnchor="end"
          fontFamily="monospace"
        >
          P-8 (2″ HW)
        </text>
        {/* Conduit - crossing */}
        <rect
          x="175"
          y="60"
          width="10"
          height="200"
          fill="rgba(234,179,8,0.5)"
          stroke="#eab308"
          strokeWidth="2"
          rx="5"
        />
        <text
          x="200"
          y="275"
          fill="#facc15"
          fontSize="11"
          fontFamily="monospace"
        >
          ET-3 (1″ EMT)
        </text>
        {/* Clash */}
        <circle
          cx="180"
          cy="143"
          r="18"
          fill="rgba(239,68,68,0.2)"
          stroke="#ef4444"
          strokeWidth="2"
        >
          <animate
            attributeName="r"
            values="16;22;16"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;0.3;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
        <text
          x="180"
          y="110"
          fill="#ef4444"
          fontSize="10"
          textAnchor="middle"
          fontWeight="bold"
        >
          CLASH
        </text>
      </svg>
    ),
    "duct-pipe": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <pattern
            id="grid3"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid3)" />
        {/* Duct */}
        <rect
          x="50"
          y="100"
          width="300"
          height="50"
          fill="rgba(249,115,22,0.3)"
          stroke="#f97316"
          strokeWidth="2"
          rx="2"
        />
        <text
          x="360"
          y="130"
          fill="#fb923c"
          fontSize="11"
          textAnchor="end"
          fontFamily="monospace"
        >
          Duct
        </text>
        {/* Pipe */}
        <rect
          x="140"
          y="50"
          width="14"
          height="220"
          fill="rgba(59,130,246,0.4)"
          stroke="#3b82f6"
          strokeWidth="2"
          rx="7"
        />
        <text
          x="170"
          y="280"
          fill="#60a5fa"
          fontSize="11"
          fontFamily="monospace"
        >
          Pipe
        </text>
        {/* Clash zones */}
        <rect
          x="136"
          y="96"
          width="22"
          height="58"
          fill="rgba(239,68,68,0.25)"
          stroke="#ef4444"
          strokeWidth="2"
          rx="3"
        >
          <animate
            attributeName="opacity"
            values="0.8;0.3;0.8"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>
      </svg>
    ),
    "sprinkler-duct": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <pattern
            id="grid4"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid4)" />
        {/* Duct */}
        <rect
          x="40"
          y="120"
          width="320"
          height="45"
          fill="rgba(249,115,22,0.3)"
          stroke="#f97316"
          strokeWidth="2"
          rx="2"
        />
        <text
          x="370"
          y="148"
          fill="#fb923c"
          fontSize="11"
          textAnchor="end"
          fontFamily="monospace"
        >
          Duct
        </text>
        {/* Sprinkler main */}
        <rect
          x="60"
          y="138"
          width="280"
          height="10"
          fill="rgba(239,68,68,0.5)"
          stroke="#dc2626"
          strokeWidth="2"
          rx="5"
        />
        {/* Branch lines */}
        {[120, 180, 240, 300].map((x) => (
          <g key={x}>
            <rect
              x={x - 2}
              y={140}
              width="4"
              height="60"
              fill="rgba(239,68,68,0.4)"
              stroke="#dc2626"
              strokeWidth="1"
              rx="2"
            />
            <circle cx={x} cy={200} r="4" fill="#dc2626" />
          </g>
        ))}
        <text
          x="60"
          y="130"
          fill="#f87171"
          fontSize="11"
          fontFamily="monospace"
        >
          SP (Fire Protection)
        </text>
        {/* Clash indicators */}
        <rect
          x="40"
          y="116"
          width="320"
          height="53"
          fill="none"
          stroke="#ef4444"
          strokeWidth="2"
          strokeDasharray="4 4"
          rx="3"
        >
          <animate
            attributeName="opacity"
            values="0.8;0.3;0.8"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>
      </svg>
    ),
    "conduit-column": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <pattern
            id="grid5"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid5)" />
        {/* Column */}
        <rect
          x="160"
          y="20"
          width="60"
          height="260"
          fill="#475569"
          stroke="#64748b"
          strokeWidth="2"
        />
        <text
          x="190"
          y="295"
          fill="#94a3b8"
          fontSize="11"
          textAnchor="middle"
          fontFamily="monospace"
        >
          C-22 (24×24)
        </text>
        {/* Conduit tray */}
        <rect
          x="60"
          y="130"
          width="260"
          height="16"
          fill="rgba(234,179,8,0.4)"
          stroke="#eab308"
          strokeWidth="2"
          rx="2"
        />
        <text
          x="335"
          y="142"
          fill="#facc15"
          fontSize="11"
          fontFamily="monospace"
        >
          ET-7
        </text>
        {/* Clash */}
        <rect
          x="158"
          y="126"
          width="64"
          height="24"
          fill="rgba(239,68,68,0.25)"
          stroke="#ef4444"
          strokeWidth="2"
          rx="3"
        >
          <animate
            attributeName="opacity"
            values="0.8;0.3;0.8"
            dur="2s"
            repeatCount="indefinite"
          />
        </rect>
        {/* Suggested path */}
        <path
          d="M 60 138 L 140 138 L 140 80 L 240 80 L 240 138 L 330 138"
          fill="none"
          stroke="#22c55e"
          strokeWidth="2"
          strokeDasharray="6 3"
        />
        <text
          x="190"
          y="72"
          fill="#22c55e"
          fontSize="10"
          textAnchor="middle"
          fontFamily="monospace"
        >
          Suggested re-route
        </text>
      </svg>
    ),
    "pipe-beam": (
      <svg viewBox="0 0 400 300" className="h-full w-full">
        <defs>
          <pattern
            id="grid6"
            width="40"
            height="40"
            patternUnits="userSpaceOnUse"
          >
            <path
              d="M 40 0 L 0 0 0 40"
              fill="none"
              stroke="rgba(255,255,255,0.05)"
              strokeWidth="1"
            />
          </pattern>
        </defs>
        <rect width="400" height="300" fill="url(#grid6)" />
        {/* Beam */}
        <rect x="50" y="120" width="300" height="6" fill="#64748b" rx="1" />
        <rect x="50" y="126" width="300" height="35" fill="#475569" rx="1" />
        <rect x="50" y="161" width="300" height="6" fill="#64748b" rx="1" />
        <text
          x="360"
          y="148"
          fill="#94a3b8"
          fontSize="11"
          textAnchor="end"
          fontFamily="monospace"
        >
          B-19
        </text>
        {/* Pipe */}
        <line
          x1="100"
          y1="60"
          x2="280"
          y2="200"
          stroke="#3b82f6"
          strokeWidth="10"
          strokeLinecap="round"
          opacity="0.5"
        />
        <line
          x1="100"
          y1="60"
          x2="280"
          y2="200"
          stroke="#3b82f6"
          strokeWidth="2"
        />
        <text
          x="290"
          y="210"
          fill="#60a5fa"
          fontSize="11"
          fontFamily="monospace"
        >
          P-12 (Storm)
        </text>
        {/* Clash */}
        <circle
          cx="185"
          cy="143"
          r="20"
          fill="rgba(239,68,68,0.2)"
          stroke="#ef4444"
          strokeWidth="2"
        >
          <animate
            attributeName="r"
            values="18;24;18"
            dur="2s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="1;0.3;1"
            dur="2s"
            repeatCount="indefinite"
          />
        </circle>
      </svg>
    ),
  };

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-lg border border-border bg-[#1a1a2e]">
      {/* Viewport toolbar */}
      <div className="flex items-center gap-1 border-b border-white/10 bg-[#16162a] px-3 py-1.5">
        <button
          className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-white/70"
          title="Isolate"
        >
          <Eye className="h-4 w-4" />
        </button>
        <button
          className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-white/70"
          title="Section Cut"
        >
          <Scissors className="h-4 w-4" />
        </button>
        <button
          className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-white/70"
          title="Fit to View"
        >
          <Maximize2 className="h-4 w-4" />
        </button>
        <button
          className="rounded p-1.5 text-white/40 hover:bg-white/10 hover:text-white/70"
          title="Orbit"
        >
          <RotateCcw className="h-4 w-4" />
        </button>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-white/30">Isolated view</span>
          <div className="h-2 w-2 rounded-full bg-green-500" />
        </div>
      </div>
      {/* Viewport content */}
      <div className="relative flex-1">
        {illustrations[clash.illustration]}
        {/* Location badge */}
        <div className="absolute bottom-3 left-3 rounded bg-black/60 px-2 py-1 text-xs text-white/60">
          {clash.location}
        </div>
      </div>
    </div>
  );
}

// ─── Setup Screen ────────────────────────────────────────────────────────────

function SetupScreen({
  attendees,
  onToggleAttendee,
  onStart,
}: {
  attendees: Attendee[];
  onToggleAttendee: (id: string) => void;
  onStart: () => void;
}) {
  const presentCount = attendees.filter((a) => a.present).length;
  const clashStats = useMemo(() => {
    const critical = CLASH_GROUPS.filter(
      (c) => c.severity === "critical",
    ).length;
    const major = CLASH_GROUPS.filter((c) => c.severity === "major").length;
    const minor = CLASH_GROUPS.filter((c) => c.severity === "minor").length;
    return { critical, major, minor, total: CLASH_GROUPS.length };
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6 py-12">
      <div className="w-full max-w-2xl space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Box className="h-4 w-4" />
            <span>Bear Clash Detection</span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Coordination Meeting
          </h1>
          <p className="text-lg text-muted-foreground">
            Riverdale Mixed-Use — Phase 2
          </p>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Clash overview */}
        <div className="rounded-lg border border-border bg-card p-5">
          <h2 className="mb-3 text-sm font-medium text-muted-foreground">
            Clashes to Review
          </h2>
          <div className="flex gap-4">
            <div className="flex-1 rounded-md border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950">
              <div className="text-2xl font-bold text-red-700 dark:text-red-400">
                {clashStats.critical}
              </div>
              <div className="text-xs text-red-600 dark:text-red-500">
                Critical
              </div>
            </div>
            <div className="flex-1 rounded-md border border-amber-200 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950">
              <div className="text-2xl font-bold text-amber-700 dark:text-amber-400">
                {clashStats.major}
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-500">
                Major
              </div>
            </div>
            <div className="flex-1 rounded-md border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950">
              <div className="text-2xl font-bold text-blue-700 dark:text-blue-400">
                {clashStats.minor}
              </div>
              <div className="text-xs text-blue-600 dark:text-blue-500">
                Minor
              </div>
            </div>
            <div className="flex-1 rounded-md border border-border bg-muted/50 p-3">
              <div className="text-2xl font-bold">{clashStats.total}</div>
              <div className="text-xs text-muted-foreground">Total Groups</div>
            </div>
          </div>
        </div>

        {/* Attendees */}
        <div className="rounded-lg border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">
              Attendees
            </h2>
            <span className="text-xs text-muted-foreground">
              {presentCount} present
            </span>
          </div>
          <div className="space-y-2">
            {attendees.map((a) => (
              <label
                key={a.id}
                className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-muted/50"
              >
                <input
                  type="checkbox"
                  checked={a.present}
                  onChange={() => onToggleAttendee(a.id)}
                  className="h-4 w-4 rounded border-border"
                />
                <div className="flex-1">
                  <span className="font-medium">{a.name}</span>
                  <span className="ml-2 text-sm text-muted-foreground">
                    — {a.company}
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className={cn("text-xs", TRADE_COLORS[a.trade])}
                >
                  {a.trade}
                </Badge>
              </label>
            ))}
          </div>
        </div>

        {/* Start */}
        <Button size="lg" className="w-full" onClick={onStart}>
          <Flashlight className="mr-2 h-4 w-4" />
          Start Walkthrough — {clashStats.total} Clash Groups
        </Button>
      </div>
    </div>
  );
}

// ─── Walkthrough Screen ──────────────────────────────────────────────────────

function WalkthroughScreen({
  clashes,
  currentIndex,
  decisions,
  attendees,
  onNavigate,
  onDecision,
  onEnd,
  meetingStartTime,
}: {
  clashes: ClashGroup[];
  currentIndex: number;
  decisions: Map<string, Decision>;
  attendees: Attendee[];
  onNavigate: (index: number) => void;
  onDecision: (decision: Decision) => void;
  onEnd: () => void;
  meetingStartTime: number;
}) {
  const clash = clashes[currentIndex]!;
  const existingDecision = decisions.get(clash.id);

  const [resolution, setResolution] = useState<ResolutionType | "">(
    existingDecision?.resolution ?? "",
  );
  const [assigneeId, setAssigneeId] = useState(
    existingDecision?.assigneeId ?? "",
  );
  const [dueDate, setDueDate] = useState(
    existingDecision?.dueDate ?? formatDueDate(7),
  );
  const [notes, setNotes] = useState(existingDecision?.notes ?? "");
  const [elapsed, setElapsed] = useState(0);
  const [chatOpen, setChatOpen] = useState(false);

  // Reset form when navigating to a new clash
  const clashId = clash.id;
  const prevClashId = useRef(clashId);
  useEffect(() => {
    if (prevClashId.current !== clashId) {
      const d = decisions.get(clashId);
      setResolution(d?.resolution ?? "");
      setAssigneeId(d?.assigneeId ?? "");
      setDueDate(d?.dueDate ?? formatDueDate(7));
      setNotes(d?.notes ?? "");
      prevClashId.current = clashId;
    }
  }, [clashId, decisions]);

  // Timer
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - meetingStartTime) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [meetingStartTime]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const presentAttendees = attendees.filter((a) => a.present);

  const handleLogDecision = useCallback(() => {
    if (!resolution) return;
    onDecision({
      clashId: clash.id,
      resolution: resolution as ResolutionType,
      assigneeId,
      dueDate,
      notes,
      timestamp: Date.now(),
    });
    if (currentIndex < clashes.length - 1) {
      onNavigate(currentIndex + 1);
    }
  }, [
    resolution,
    assigneeId,
    dueDate,
    notes,
    clash.id,
    onDecision,
    onNavigate,
    currentIndex,
    clashes.length,
  ]);

  const sevConfig = SEVERITY_CONFIG[clash.severity];

  return (
    <div className="flex h-screen flex-col bg-background">
      {/* Top bar */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Box className="h-4 w-4" />
            <span className="font-medium">Coordination Meeting</span>
          </div>
          <div className="h-4 w-px bg-border" />
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              disabled={currentIndex === 0}
              onClick={() => onNavigate(currentIndex - 1)}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="min-w-[100px] text-center text-sm font-medium">
              {currentIndex + 1} of {clashes.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              disabled={currentIndex === clashes.length - 1}
              onClick={() => onNavigate(currentIndex + 1)}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="h-4 w-px bg-border" />
          <Badge
            variant="outline"
            className={cn("text-xs", sevConfig.bg, sevConfig.color)}
          >
            {sevConfig.label}
          </Badge>
          {/* Progress dots */}
          <div className="flex items-center gap-1">
            {clashes.map((c, i) => (
              <button
                key={c.id}
                onClick={() => onNavigate(i)}
                className={cn(
                  "h-2 w-2 rounded-full transition-colors",
                  i === currentIndex
                    ? "bg-foreground"
                    : decisions.has(c.id)
                      ? "bg-green-500"
                      : "bg-muted-foreground/30",
                )}
                title={`${c.id}: ${c.title}`}
              />
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            {formatTime(elapsed)}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Users className="h-3.5 w-3.5" />
            {presentAttendees.length}
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {decisions.size}/{clashes.length}
          </div>
          <Button
            variant={chatOpen ? "secondary" : "outline"}
            size="sm"
            onClick={() => setChatOpen(!chatOpen)}
          >
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
            AI Chat
          </Button>
          <Button variant="outline" size="sm" onClick={onEnd}>
            End Meeting
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left panel — Details + Decision */}
        <div className="flex w-[480px] shrink-0 flex-col overflow-y-auto border-r border-border">
          <div className="space-y-4 p-4">
            {/* Clash header */}
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                {clash.id}
              </div>
              <h2 className="text-lg font-semibold leading-tight">
                {clash.title}
              </h2>
              <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {clash.intersectionCount} intersection
                  {clash.intersectionCount > 1 ? "s" : ""}
                </span>
                <span>·</span>
                <span>{clash.location}</span>
              </div>
            </div>

            {/* Elements involved */}
            <div className="rounded-md border border-border">
              <div className="border-b border-border bg-muted/30 px-3 py-2 text-xs font-medium text-muted-foreground">
                Elements Involved
              </div>
              {clash.elements.map((el) => (
                <div
                  key={el.id}
                  className="flex items-center gap-3 border-b border-border px-3 py-2 last:border-b-0"
                >
                  <code className="text-xs font-bold">{el.id}</code>
                  <span className="flex-1 text-sm">{el.description}</span>
                  <Badge
                    variant="outline"
                    className={cn("text-xs", TRADE_COLORS[el.system])}
                  >
                    {el.system}
                  </Badge>
                </div>
              ))}
            </div>

            {/* Severity rationale */}
            <div className={cn("rounded-md border p-3", sevConfig.bg)}>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-medium">
                <AlertTriangle className="h-3.5 w-3.5" />
                Severity: {sevConfig.label}
              </div>
              <p className="text-sm leading-relaxed">
                {clash.severityRationale}
              </p>
            </div>

            {/* AI Suggestion */}
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 dark:border-emerald-900 dark:bg-emerald-950">
              <div className="mb-1 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                  <Sparkles className="h-3.5 w-3.5" />
                  AI Suggestion
                </div>
                <Badge
                  variant="outline"
                  className="border-emerald-300 text-xs text-emerald-700 dark:border-emerald-800 dark:text-emerald-400"
                >
                  {clash.aiConfidence}% confidence
                </Badge>
              </div>
              <p className="mb-2 text-sm leading-relaxed text-emerald-900 dark:text-emerald-200">
                {clash.aiSuggestion}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-800 dark:text-emerald-400 dark:hover:bg-emerald-900"
              >
                <Eye className="mr-1.5 h-3.5 w-3.5" />
                Preview in Model
              </Button>
            </div>

            {/* Decision form */}
            <div className="rounded-md border border-border bg-card p-3">
              <div className="mb-3 text-xs font-medium text-muted-foreground">
                Decision
              </div>
              <div className="space-y-3">
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Resolution
                  </label>
                  <Select
                    value={resolution}
                    onValueChange={(v) => setResolution(v as ResolutionType)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select resolution..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(RESOLUTION_LABELS).map(
                        ([value, label]) => (
                          <SelectItem key={value} value={value}>
                            {label}
                          </SelectItem>
                        ),
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Assign to
                    </label>
                    <Select value={assigneeId} onValueChange={setAssigneeId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent>
                        {presentAttendees.map((a) => (
                          <SelectItem key={a.id} value={a.id}>
                            {a.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs text-muted-foreground">
                      Due date
                    </label>
                    <input
                      type="date"
                      value={dueDate}
                      onChange={(e) => setDueDate(e.target.value)}
                      className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    />
                  </div>
                </div>
                <div>
                  <label className="mb-1 block text-xs text-muted-foreground">
                    Notes
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Meeting notes for this clash..."
                    rows={3}
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
                <Button
                  className="w-full"
                  disabled={!resolution}
                  onClick={handleLogDecision}
                >
                  {decisions.has(clash.id) ? (
                    <>
                      <Check className="mr-1.5 h-4 w-4" />
                      Update Decision
                    </>
                  ) : currentIndex < clashes.length - 1 ? (
                    <>
                      Log Decision & Next
                      <ArrowRight className="ml-1.5 h-4 w-4" />
                    </>
                  ) : (
                    <>
                      <Check className="mr-1.5 h-4 w-4" />
                      Log Final Decision
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Right panel — 3D viewport mock */}
        <div className="flex-1 p-4">
          <ViewportMock clash={clash} />
        </div>

        {/* Chat panel */}
        {chatOpen && (
          <div className="w-96 shrink-0 border-l border-border">
            <ChatPane onClose={() => setChatOpen(false)} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Summary Screen ──────────────────────────────────────────────────────────

function SummaryScreen({
  clashes,
  decisions,
  attendees,
  meetingStartTime,
  onClose,
}: {
  clashes: ClashGroup[];
  decisions: Map<string, Decision>;
  attendees: Attendee[];
  meetingStartTime: number;
  onClose: () => void;
}) {
  const duration = Math.floor((Date.now() - meetingStartTime) / 1000);
  const presentAttendees = attendees.filter((a) => a.present);

  const stats = useMemo(() => {
    let resolved = 0;
    let deferred = 0;
    let needsInfo = 0;
    let accepted = 0;
    let unreviewed = 0;
    for (const clash of clashes) {
      const d = decisions.get(clash.id);
      if (!d) {
        unreviewed++;
      } else if (
        d.resolution === "approve_suggestion" ||
        d.resolution === "modify_approve"
      ) {
        resolved++;
      } else if (d.resolution === "defer") {
        deferred++;
      } else if (d.resolution === "needs_info") {
        needsInfo++;
      } else if (d.resolution === "accept_as_is") {
        accepted++;
      }
    }
    return { resolved, deferred, needsInfo, accepted, unreviewed };
  }, [clashes, decisions]);

  // Group action items by assignee
  const actionItems = useMemo(() => {
    const byAssignee = new Map<
      string,
      { clash: ClashGroup; decision: Decision }[]
    >();
    for (const clash of clashes) {
      const d = decisions.get(clash.id);
      if (!d || !d.assigneeId) continue;
      const list = byAssignee.get(d.assigneeId) ?? [];
      list.push({ clash, decision: d });
      byAssignee.set(d.assigneeId, list);
    }
    return byAssignee;
  }, [clashes, decisions]);

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    return `${m} min`;
  };

  return (
    <div className="min-h-screen bg-background px-6 py-12">
      <div className="mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Box className="h-4 w-4" />
            <span>Bear Clash Detection</span>
          </div>
          <h1 className="text-3xl font-semibold tracking-tight">
            Meeting Summary
          </h1>
          <p className="text-muted-foreground">
            Riverdale Mixed-Use — Phase 2 ·{" "}
            {new Date().toLocaleDateString("en-US", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            })}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-5 gap-3">
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {stats.resolved}
            </div>
            <div className="text-xs text-muted-foreground">Resolved</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">
              {stats.deferred}
            </div>
            <div className="text-xs text-muted-foreground">Deferred</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {stats.needsInfo}
            </div>
            <div className="text-xs text-muted-foreground">Needs Info</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-slate-600">
              {stats.accepted}
            </div>
            <div className="text-xs text-muted-foreground">Accepted</div>
          </div>
          <div className="rounded-lg border border-border bg-card p-4 text-center">
            <div className="text-2xl font-bold text-muted-foreground">
              {stats.unreviewed}
            </div>
            <div className="text-xs text-muted-foreground">Unreviewed</div>
          </div>
        </div>

        {/* Meeting info */}
        <div className="flex gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            Duration: {formatDuration(duration)}
          </div>
          <div className="flex items-center gap-1.5">
            <Users className="h-4 w-4" />
            {presentAttendees.length} attendees
          </div>
          <div className="flex items-center gap-1.5">
            <MessageSquare className="h-4 w-4" />
            {decisions.size} decisions recorded
          </div>
        </div>

        {/* Decisions table */}
        <div className="rounded-lg border border-border">
          <div className="border-b border-border bg-muted/30 px-4 py-3">
            <h2 className="text-sm font-medium">All Clash Decisions</h2>
          </div>
          <div className="divide-y divide-border">
            {clashes.map((clash) => {
              const d = decisions.get(clash.id);
              const assignee = d
                ? attendees.find((a) => a.id === d.assigneeId)
                : null;
              const sev = SEVERITY_CONFIG[clash.severity];
              return (
                <div
                  key={clash.id}
                  className="flex items-center gap-4 px-4 py-3"
                >
                  <div className="w-16 shrink-0">
                    <code className="text-xs text-muted-foreground">
                      {clash.id}
                    </code>
                  </div>
                  <Badge
                    variant="outline"
                    className={cn(
                      "w-16 justify-center text-xs",
                      sev.bg,
                      sev.color,
                    )}
                  >
                    {sev.label}
                  </Badge>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {clash.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {clash.location}
                    </div>
                  </div>
                  {d ? (
                    <>
                      <Badge
                        variant="outline"
                        className={cn("text-xs", {
                          "border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-400":
                            d.resolution === "approve_suggestion" ||
                            d.resolution === "modify_approve",
                          "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-400":
                            d.resolution === "defer",
                          "border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-400":
                            d.resolution === "needs_info",
                          "border-slate-300 bg-slate-50 text-slate-700 dark:border-slate-800 dark:bg-slate-950 dark:text-slate-400":
                            d.resolution === "accept_as_is",
                        })}
                      >
                        {RESOLUTION_LABELS[d.resolution]}
                      </Badge>
                      {assignee && (
                        <span className="w-28 truncate text-right text-xs text-muted-foreground">
                          → {assignee.name}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Not reviewed
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Action items by assignee */}
        {actionItems.size > 0 && (
          <div className="rounded-lg border border-border">
            <div className="border-b border-border bg-muted/30 px-4 py-3">
              <h2 className="text-sm font-medium">Action Items by Assignee</h2>
            </div>
            <div className="divide-y divide-border">
              {Array.from(actionItems.entries()).map(([assigneeId, items]) => {
                const assignee = attendees.find((a) => a.id === assigneeId);
                if (!assignee) return null;
                return (
                  <div key={assigneeId} className="px-4 py-3">
                    <div className="mb-2 flex items-center gap-2">
                      <span className="font-medium">{assignee.name}</span>
                      <Badge
                        variant="outline"
                        className={cn("text-xs", TRADE_COLORS[assignee.trade])}
                      >
                        {assignee.trade}
                      </Badge>
                      <span className="text-xs text-muted-foreground">
                        — {items.length} item{items.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="space-y-1.5 pl-4">
                      {items.map(({ clash, decision }) => (
                        <div
                          key={clash.id}
                          className="flex items-baseline gap-2 text-sm"
                        >
                          <code className="text-xs text-muted-foreground">
                            {clash.id}
                          </code>
                          <span>{clash.title}</span>
                          {decision.dueDate && (
                            <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                              Due {decision.dueDate}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onClose}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Setup
          </Button>
          <Button variant="outline">
            <Download className="mr-1.5 h-4 w-4" />
            Export Minutes (PDF)
          </Button>
          <Button variant="outline">
            <FileText className="mr-1.5 h-4 w-4" />
            Export Minutes (CSV)
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

function ClashMeeting() {
  const [phase, setPhase] = useState<MeetingPhase>("setup");
  const [attendees, setAttendees] = useState<Attendee[]>(ATTENDEES);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [decisions, setDecisions] = useState<Map<string, Decision>>(new Map());
  const [meetingStartTime, setMeetingStartTime] = useState(0);

  const handleToggleAttendee = useCallback((id: string) => {
    setAttendees((prev) =>
      prev.map((a) => (a.id === id ? { ...a, present: !a.present } : a)),
    );
  }, []);

  const handleStart = useCallback(() => {
    setMeetingStartTime(Date.now());
    setPhase("walkthrough");
  }, []);

  const handleDecision = useCallback((decision: Decision) => {
    setDecisions((prev) => {
      const next = new Map(prev);
      next.set(decision.clashId, decision);
      return next;
    });
  }, []);

  const handleEnd = useCallback(() => {
    setPhase("summary");
  }, []);

  const handleClose = useCallback(() => {
    setPhase("setup");
    setCurrentIndex(0);
    setDecisions(new Map());
  }, []);

  if (phase === "setup") {
    return (
      <SetupScreen
        attendees={attendees}
        onToggleAttendee={handleToggleAttendee}
        onStart={handleStart}
      />
    );
  }

  if (phase === "walkthrough") {
    return (
      <WalkthroughScreen
        clashes={CLASH_GROUPS}
        currentIndex={currentIndex}
        decisions={decisions}
        attendees={attendees}
        onNavigate={setCurrentIndex}
        onDecision={handleDecision}
        onEnd={handleEnd}
        meetingStartTime={meetingStartTime}
      />
    );
  }

  return (
    <SummaryScreen
      clashes={CLASH_GROUPS}
      decisions={decisions}
      attendees={attendees}
      meetingStartTime={meetingStartTime}
      onClose={handleClose}
    />
  );
}

// ─── Chat Pane ───────────────────────────────────────────────────────────────

const CHAT_INITIAL_MESSAGES: ChatMessage[] = [
  {
    id: "1",
    role: "assistant",
    text: "Hi! I can help you understand the clashes in this coordination meeting. Ask me about specific clashes, trade conflicts, resolution strategies, or how these issues affect the project schedule.",
  },
];

const CHAT_MOCK_RESPONSES = [
  "The beam-duct conflict in the 2nd floor mechanical room is the most critical. The 24×12 supply duct needs to drop below the W12×26 beam, but there's only 8 inches of clearance. Rerouting the duct south adds 12 feet of run but avoids any structural modifications.",
  "There are 3 clashes involving the mechanical trade (AirFlow MEP) and 2 involving electrical (BrightWire). The mechanical clashes are higher severity because ductwork is harder to reroute than conduit.",
  "Deferring CLH-004 is reasonable — the sprinkler head relocation depends on the final ceiling grid layout, which isn't finalized until next week. Flag it for the May coordination meeting.",
  "The AI suggestion for CLH-001 has 94% confidence because it's a straightforward duct reroute with no downstream impacts. CLH-003 is lower confidence (78%) because the conduit reroute could affect panel schedule pull lengths.",
  "Based on similar projects, resolving these 7 clashes typically takes 2–3 weeks of design revision. The critical ones (CLH-001, CLH-002) should be prioritized to avoid delaying the mechanical rough-in, currently scheduled for May 15.",
];

function ChatPane({ onClose }: { onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>(
    CHAT_INITIAL_MESSAGES,
  );
  const [input, setInput] = useState("");
  const [isThinking, setIsThinking] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const responseIdx = useRef(0);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      const text = input.trim();
      if (!text || isThinking) return;

      const userMsg: ChatMessage = {
        id: String(Date.now()),
        role: "user",
        text,
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setIsThinking(true);

      // Fake response after a short delay
      setTimeout(() => {
        const response =
          CHAT_MOCK_RESPONSES[
            responseIdx.current % CHAT_MOCK_RESPONSES.length
          ]!;
        responseIdx.current++;
        setMessages((prev) => [
          ...prev,
          { id: String(Date.now()), role: "assistant", text: response },
        ]);
        setIsThinking(false);
        requestAnimationFrame(() => {
          scrollRef.current?.scrollTo({
            top: scrollRef.current.scrollHeight,
            behavior: "smooth",
          });
        });
      }, 800);

      requestAnimationFrame(() => {
        scrollRef.current?.scrollTo({
          top: scrollRef.current.scrollHeight,
          behavior: "smooth",
        });
      });
    },
    [input, isThinking],
  );

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <div className="text-sm font-medium">Ask about these clashes</div>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6"
          onClick={onClose}
        >
          <X className="h-3.5 w-3.5" />
        </Button>
      </div>
      <div ref={scrollRef} className="flex-1 space-y-4 overflow-auto p-4">
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={cn(
              "max-w-[85%] rounded-lg px-3 py-2 text-sm",
              msg.role === "user"
                ? "ml-auto bg-primary text-primary-foreground"
                : "bg-muted",
            )}
          >
            {msg.text}
          </div>
        ))}
        {isThinking && (
          <div className="max-w-[85%] rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground">
            Thinking...
          </div>
        )}
      </div>
      <form onSubmit={handleSubmit} className="border-t p-3">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask a question..."
            className="flex-1"
          />
          <Button
            type="submit"
            size="icon"
            disabled={!input.trim() || isThinking}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}
